import { useUpdateVetterUserMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

export interface MemberData {
	id: string;
	firstName: string;
	lastName: string;
	email?: string | null;
	phone?: string | null;
	notes?: string | null;
}

export function EditMemberModal({
	open,
	onOpenChange,
	member,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	member: MemberData | null;
	onSuccess?: () => void;
}) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [phone, setPhone] = useState('');
	const [notes, setNotes] = useState('');

	const [updateVetterUser, { loading }] = useUpdateVetterUserMutation();

	useEffect(() => {
		if (open && member) {
			setFirstName(member.firstName);
			setLastName(member.lastName);
			setPhone(member.phone || '');
			setNotes(member.notes || '');
		}
	}, [open, member]);

	const handleSubmit = async () => {
		if (!member || !firstName.trim() || !lastName.trim()) return;

		try {
			const { data } = await updateVetterUser({
				variables: {
					id: member.id,
					input: {
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						// null (not undefined) so emptying a field actually clears it
						phone: phone.trim() || null,
						notes: notes.trim() || null,
					},
				},
			});

			if (data?.updateVetterUser?.success) {
				toast.success('Team member updated successfully');
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.updateVetterUser?.message || 'Failed to update team member');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update team member');
		}
	};

	const canSubmit = firstName.trim() && lastName.trim();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit team member</DialogTitle>
					<DialogDescription>
						Update this team member’s name and contact details.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{member?.email && (
								<div className="flex flex-col gap-1.5">
									<Label>Email</Label>
									<Input value={member.email} disabled readOnly />
								</div>
							)}

							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="edit-member-first-name">
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="edit-member-first-name"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="First name"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="edit-member-last-name">
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="edit-member-last-name"
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Last name"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-member-phone">Phone</Label>
								<Input
									id="edit-member-phone"
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									placeholder="+45 12 34 56 78"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-member-notes">Notes</Label>
								<Textarea
									id="edit-member-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Role, availability, anything worth remembering"
									rows={3}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Saving…' : 'Save changes'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
